import { Alert, Group, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { Button } from 'src/design-system/Button';
import { useFlags } from 'src/feature-flags/FlagsHook';
import type { MeetingNoteStaleness } from 'src/meeting-note/types';
import { useRegenerateMeetingNote } from 'src/meetings/hooks/useRegenerateMeetingNote';
import type { CalendarEvent } from 'src/meetings/models';

type Props = {
  event: CalendarEvent;
};

// Naming the wrong people and putting a statement in the wrong mouth are different mistakes, and an
// adviser checking the note needs to know which one to look for, so each reason gets its own wording.
const STALENESS_MESSAGE: Record<MeetingNoteStaleness, string> = {
  ATTENDEES:
    'The attendees have changed since this meeting note was generated, so it may name the wrong people.',
  SPEAKERS:
    "The transcript's speakers have changed since this meeting note was generated, so it may attribute statements to the wrong person.",
  ATTENDEES_AND_SPEAKERS:
    "The attendees and the transcript's speakers have changed since this meeting note was generated, so it may name the wrong people and attribute statements to the wrong person.",
};

export function MeetingNoteOutOfDateBanner({ event }: Props) {
  const { flagEnabledAttendeeChangesRefreshOutputs, flagEnabledSpeakerChangesRefreshNote } =
    useFlags();
  const { meetingNote, canRegenerate, regenerate, isRegenerating } =
    useRegenerateMeetingNote(event);

  const anyFlagOn =
    flagEnabledAttendeeChangesRefreshOutputs || flagEnabledSpeakerChangesRefreshNote;
  if (!anyFlagOn || !meetingNote?.isOutOfDate || !canRegenerate) {
    return null;
  }

  // A current API always sends the reason alongside isOutOfDate, so this falls back only for a website released
  // ahead of the API, which reports staleness without saying why — and only ever meant the attendees.
  const message = STALENESS_MESSAGE[meetingNote.outOfDateReason ?? 'ATTENDEES'];

  return (
    <Alert variant="light" color="yellow" icon={<IconAlertTriangle size={18} />} mb="md">
      <Group justify="space-between" wrap="nowrap" gap="md">
        <Text size="sm">{message}</Text>
        <Button variant="secondary" size="sm" onClick={regenerate} loading={isRegenerating}>
          Regenerate meeting note
        </Button>
      </Group>
    </Alert>
  );
}
